/**
 * AssistantDock — omnipresent "Ask your forest" panel on every protected route.
 *
 * E1 ships: dock panel with open/close state.
 * Trigger: the top bar's Ask control (FmTopBar), shown while a route serves an
 * assistant — one shell entry, never a floating button over the page.
 * Body: a discriminated shared-content renderer with a generic placeholder
 * fallback. Route-specific context is adapted before it reaches this shell.
 *
 * Consolidated to a single Ask affordance.
 */
import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { useAssistantDock } from '@/contexts/AssistantDockContext';
import type {
  AssistantContextSeedDescriptor,
  AssistantDockContent,
} from '@/contexts/AssistantDockContext';
import type { AssistantContentDescriptor } from '@/contexts/planAssistant.types';
import { planAssistantContextIdentity } from '@/contexts/planAssistant.types';
import { FmStack, FmText } from '@/components/ui';
import { PlanAssistantConversation } from '@/shared/ui/PlanAssistantConversation';
import { useLanguage } from '@/i18n';
import './AssistantDock.css';
import { fmMapOcclusion } from '@/shared/ui/mapOcclusion';

// AssistantSurface seam
// The surface remains the generic unseeded fallback. Seeded conversations use
// the explicit AssistantContentDescriptor renderer below.
export interface AssistantSurface {
  /** Render the assistant panel body. */
  render(): React.ReactNode;
}

/** Default placeholder body — clearly labelled; not fabricated forest data. */
export const AssistantPlaceholder: React.FC = () => {
  const { t } = useLanguage();
  const a = t.shell.assistant;
  return (
    <div className="assistant-placeholder" data-testid="assistant-placeholder">
      <p className="assistant-placeholder__title">{a.title}</p>
      <p className="assistant-placeholder__note">{a.placeholderNote}</p>
      <p className="assistant-placeholder__hint">{a.placeholderHint}</p>
    </div>
  );
};

const defaultSurface: AssistantSurface = {
  render: () => <AssistantPlaceholder />,
};

// The panel header already names the context (descriptor.title), so the body
// opens with what the answers are based on rather than the same title again.
const AssistantContextHeader: React.FC<{ descriptor: AssistantContextSeedDescriptor }> = ({
  descriptor,
}) => (
  <FmText size="caption" tone="subtle">
    {descriptor.summary}
  </FmText>
);

const AssistantContentRenderer: React.FC<{ content: AssistantContentDescriptor }> = ({
  content,
}) => {
  switch (content.kind) {
    case 'plan-conversation':
      return (
        <PlanAssistantConversation
          key={planAssistantContextIdentity(content.context)}
          context={content.context}
          ask={content.ask}
          onContextExpired={content.onContextExpired}
        />
      );
    case 'placeholder':
      return <AssistantPlaceholder />;
  }
};

const renderDockContent = (
  content: AssistantDockContent | null,
  surface: AssistantSurface,
): React.ReactNode => {
  if (content === null || content.kind === 'default') return surface.render();
  if (content.kind === 'node') return content.body;
  return (
    <FmStack
      gap="md"
      data-testid="assistant-context-seed"
      data-assistant-content-kind={content.assistantContent.kind}
      data-assistant-plan-id={
        content.assistantContent.kind === 'plan-conversation'
          ? content.assistantContent.context.planId
          : undefined
      }
    >
      <AssistantContextHeader descriptor={content.descriptor} />
      <AssistantContentRenderer content={content.assistantContent} />
    </FmStack>
  );
};

// AssistantDock component

interface AssistantDockProps {
  /** Override the panel body. Defaults to AssistantPlaceholder. */
  surface?: AssistantSurface;
}

const AssistantDock: React.FC<AssistantDockProps> = ({ surface = defaultSurface }) => {
  const { isOpen, content, close } = useAssistantDock();
  const { t } = useLanguage();
  const a = t.shell.assistant;
  const body = renderDockContent(content, surface);
  const panelTitle = content?.kind === 'context' ? content.descriptor.title : a.title;

  return (
    <div className="assistant-dock" data-testid="assistant-dock">
      {isOpen && (
        <dialog
          className="assistant-dock__panel"
          data-testid="assistant-dock-panel"
          {...fmMapOcclusion('right', 'panel')}
          open
          aria-label={panelTitle}
          aria-modal="false"
        >
          <div className="assistant-dock__header">
            <span className="assistant-dock__panel-title">{panelTitle}</span>
            <button
              type="button"
              className="assistant-dock__close-btn"
              data-testid="assistant-dock-close"
              onClick={close}
              aria-label={a.close}
            >
              <CloseOutlined />
            </button>
          </div>
          <div className="assistant-dock__body">{body}</div>
        </dialog>
      )}
    </div>
  );
};

export default AssistantDock;
